/**
 * Sidebar v2 drag: the below-the-pins clamp, the section-aware collision
 * detector, and the sorting strategy that previews the committed layout
 * while a row is lifted.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.drag.ts` at
 * pin e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: forge does not carry
 * @dnd-kit/modifiers, so `restrictToVerticalAxis` and
 * `restrictToFirstScrollableAncestor` are implemented here with the same
 * semantics instead of adding the dependency; the collision and projection
 * closures are decomposed into named helpers.
 */
import { closestCenter, type CollisionDetection, type Modifier } from "@dnd-kit/core";

import {
  resolveSidebarDropTarget,
  sidebarMarkerId,
  type SidebarListItem,
  type SidebarSection,
} from "./sidebarV2List";

// ── Local replacements for @dnd-kit/modifiers ────────────────────────

/** Lock the lifted row to the vertical axis: horizontal movement never
 * contributes to the transform. */
export const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

/** Clamp a drag transform to keep the translated row within the supplied bounding rectangle. */
function restrictToBoundingBox(
  rect: { top: number; bottom: number; left: number; right: number },
  boundingRect: { top: number; height: number; left: number; width: number },
  transform: DragTransform,
) {
  const value = { ...transform };
  if (rect.top + transform.y <= boundingRect.top) {
    value.y = boundingRect.top - rect.top;
  } else if (rect.bottom + transform.y >= boundingRect.top + boundingRect.height) {
    value.y = boundingRect.top + boundingRect.height - rect.bottom;
  }
  if (rect.left + transform.x <= boundingRect.left) {
    value.x = boundingRect.left - rect.left;
  } else if (rect.right + transform.x >= boundingRect.left + boundingRect.width) {
    value.x = boundingRect.left + boundingRect.width - rect.right;
  }
  return value;
}

/** Keep the lifted row inside the first scrollable ancestor so the drag
 * cannot leave the sidebar's scroll viewport. */
export const restrictToFirstScrollableAncestor: Modifier = ({
  draggingNodeRect,
  transform,
  scrollableAncestors,
}) => {
  const firstScrollableAncestor = scrollableAncestors?.[0];
  if (!firstScrollableAncestor || !draggingNodeRect) return transform;
  return restrictToBoundingBox(
    draggingNodeRect,
    firstScrollableAncestor.getBoundingClientRect(),
    transform,
  );
};

// ── Ported from upstream Sidebar.drag.ts ─────────────────────────────

/** Keep the lifted card below the Pins label, including when Pins is empty.
 * The container rect follows scrolling; the offset is measured once at pickup. */
export function restrictBelowSidebarLabel(
  { transform, containerNodeRect, draggingNodeRect }: Parameters<Modifier>[0],
  offset: number,
) {
  if (!containerNodeRect || !draggingNodeRect) return transform;
  const minimumY = containerNodeRect.top + offset - draggingNodeRect.top;
  return transform.y < minimumY ? { ...transform, y: minimumY } : transform;
}

type DragArgs = Parameters<CollisionDetection>[0];

/** Structural twin of @dnd-kit/core's Transform, which this version does
 * not export as a named type. */
export interface DragTransform {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
}

/** The divider rect the pointer must cross to flip between pinned and
 * active, read off the boundary label's rendered element. */
function readBoundaryRect(args: DragArgs) {
  return (
    args.droppableContainers
      .find((container) => container.id === sidebarMarkerId("pinned-divider"))
      ?.node.current?.querySelector(".sidebar-drag-boundary-label")
      ?.getBoundingClientRect() ?? null
  );
}

/** The top of the first shelf header below the inbox, or null. */
function readShelfTop(args: DragArgs): number | null {
  const nextHeader =
    args.droppableContainers.find(
      (container) => container.id === sidebarMarkerId("snoozed-header"),
    ) ??
    args.droppableContainers.find(
      (container) => container.id === sidebarMarkerId("settled-header"),
    );
  return nextHeader?.node.current?.getBoundingClientRect().top ?? null;
}

/** Which side of the divider the pointer moved onto. Only pointer movement
 * flips the section: a stationary gesture must not toggle back when the
 * previewed layout reflows under it. */
function flippedBoundarySection(
  current: "pinned" | "active",
  boundary: { top: number; bottom: number; left: number; right: number },
  previousY: number,
  pointer: { x: number; y: number },
): "pinned" | "active" {
  if (pointer.x < boundary.left || pointer.x > boundary.right) return current;
  if (pointer.y < previousY && pointer.y <= boundary.bottom) return "pinned";
  if (pointer.y > previousY && pointer.y >= boundary.top) return "active";
  return current;
}

/** Promote the first collision whose section matches the pointer's side of
 * the divider, so crossing always lands a legal target. */
function promoteBoundarySectionTarget(
  collisions: ReturnType<typeof closestCenter>,
  boundarySection: "pinned" | "active",
  args: DragArgs,
  items: readonly SidebarListItem[],
  sections: Map<string, SidebarSection | null>,
  activeBottom: number | null,
  pointer: { x: number; y: number } | null,
): ReturnType<typeof closestCenter> {
  const inPinnedZone = boundarySection === "pinned";
  const aboveShelf = activeBottom != null && pointer !== null && pointer.y < activeBottom;
  if (!inPinnedZone && !aboveShelf) return collisions;
  const target = collisions.find((collision) => {
    const id = String(collision.id);
    if (!sections.has(id)) {
      sections.set(
        id,
        resolveSidebarDropTarget(items, String(args.active.id), id)?.section ?? null,
      );
    }
    return sections.get(id) === boundarySection;
  });
  if (!target) return collisions;
  return [target, ...collisions.filter((collision) => collision !== target)];
}

/** Drop the nearest collision when its target is unsupported, keeping the
 * lifted row selectable so the gesture can end where it started. */
function enforceNearestValidity(
  collisions: ReturnType<typeof closestCenter>,
  args: DragArgs,
  validity: Map<string, boolean>,
  isValidTarget: (id: string) => boolean,
): ReturnType<typeof closestCenter> {
  const nearest = collisions[0];
  if (!nearest || nearest.id === args.active.id) return collisions;
  const id = String(nearest.id);
  const valid = validity.get(id) ?? isValidTarget(id);
  validity.set(id, valid);
  return valid ? collisions : collisions.filter((collision) => collision.id === args.active.id);
}

/** Reject the nearest unsupported target without selecting another section.
 * Recreate this detector when drop eligibility changes. */
export function createSidebarCollisionDetection(
  isValidTarget: (id: string) => boolean,
  options: {
    items?: readonly SidebarListItem[];
    activationY?: number | null;
  } = {},
): CollisionDetection {
  const validity = new Map<string, boolean>();
  const sections = new Map<string, SidebarSection | null>();
  let previousPointerY = options.activationY;
  let boundarySection: "pinned" | "active" | undefined;
  // fallow-ignore-next-line complexity -- byte-faithful upstream collision closure, decomposed
  return (args) => {
    let collisions = closestCenter(args);
    const pointer = args.pointerCoordinates;
    const items = options.items;
    const source = items?.find((item) => item.kind === "thread" && item.key === args.active.id);
    const boundary = readBoundaryRect(args);
    if (items && boundary && source?.kind === "thread" && pointer) {
      boundarySection ??= source.section === "pinned" ? "pinned" : "active";
      // The visible divider row, including its sortable translation, is the
      // section gate: only pointer movement flips it — a stationary pointer
      // must not toggle back when the previewed layout reflows.
      const previousY = previousPointerY ?? pointer.y;
      previousPointerY = pointer.y;
      if (pointer.x >= boundary.left && pointer.x <= boundary.right) {
        boundarySection = flippedBoundarySection(boundarySection, boundary, previousY, pointer);
        const activeBottom = readShelfTop(args);
        if (boundarySection === "pinned" || (activeBottom != null && pointer.y < activeBottom)) {
          collisions = promoteBoundarySectionTarget(
            collisions,
            boundarySection,
            args,
            items,
            sections,
            activeBottom,
            pointer,
          );
        }
      }
    }
    return enforceNearestValidity(collisions, args, validity, isValidTarget);
  };
}
